import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./LeadCapture.css";

const FUNCTIONS_BASE =
  (typeof window !== "undefined" && window.__FN_BASE__) ||
  process.env.REACT_APP_FUNCTIONS_BASE_URL ||
  "/api";

function emailOK(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((v || "").trim());
}

export default function LeadCapture() {
  const nav = useNavigate();
  const loc = useLocation();

  const sp = useMemo(() => new URLSearchParams(loc.search || ""), [loc.search]);
  const [email, setEmail] = useState((sp.get("email") || "").trim());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const utm = useMemo(() => {
    // Keep UTM keys in the format your backend expects (supports both styles)
    return {
      utm_source: sp.get("utm_source") || "",
      utm_medium: sp.get("utm_medium") || "",
      utm_campaign: sp.get("utm_campaign") || "",
      utm_term: sp.get("utm_term") || "",
      utm_content: sp.get("utm_content") || "",
      gclid: sp.get("gclid") || "",
      fbclid: sp.get("fbclid") || "",
    };
  }, [sp]);

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");

    const em = (email || "").trim().toLowerCase();
    if (!emailOK(em)) {
      setErr("Please enter a valid email address.");
      return;
    }

    setBusy(true);
    try {
      // Persist email so Register can prefill it later (even if query params get lost)
      try {
        sessionStorage.setItem("lwb_lead_email", em);
        localStorage.setItem("lwb_lead_email", em);
      } catch {}

      const r = await fetch(`${FUNCTIONS_BASE}/leads/capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: em,
          stage: "email_captured",
          page: "/start",
          utm,
          referrer: document.referrer || "",
        }),
      });

      const ct = (r.headers.get("content-type") || "").toLowerCase();
      if (!ct.includes("application/json")) {
        throw new Error(
          "Lead capture endpoint is not returning JSON. This usually means /api/leads/capture is not rewired to the Cloud Function in firebase.json."
        );
      }

      const data = await r.json().catch(() => ({}));
      if (!r.ok || data?.ok !== true) {
        throw new Error(data?.error || `HTTP_${r.status}`);
      }

      // Keep existing query params (utm/gclid/etc) and also include email
      const next = new URLSearchParams(loc.search || "");
      next.set("email", em);

      // funnel order: start -> plans
      nav(`/plans?${next.toString()}`, { replace: true });
    } catch (e2) {
      setErr(e2?.message || "Could not save your email.");
    } finally {
      setBusy(false);
    }
  }

  const logoSrc = `${process.env.PUBLIC_URL}/Lastwishbox-logo.png`;

  return (
    <div className="lwb-start">
      <div className="lwb-start__bg" aria-hidden="true" />

      <div className="lwb-start__wrap">
        <div className="lwb-start__card">
          {/* Left: form */}
          <div className="lwb-start__left">
            <img
              className="lwb-start__logo"
              src={logoSrc}
              alt="LastWishBox"
              loading="eager"
            />

            <h1 className="lwb-start__title">Get started</h1>
            <p className="lwb-start__subtitle">
              Enter your email to continue. You’ll choose a plan next.
            </p>

            <form className="lwb-start__form" onSubmit={onSubmit}>
              <label className="lwb-start__label" htmlFor="lwb_email">
                Email
              </label>

              <div className={`lwb-start__field ${err ? "has-error" : ""}`}>
                <input
                  id="lwb_email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  inputMode="email"
                  spellCheck={false}
                  className="lwb-start__input"
                  disabled={busy}
                />
              </div>

              {err ? <div className="lwb-start__error">{err}</div> : null}

              <button
                type="submit"
                disabled={busy}
                className="lwb-start__btn"
              >
                {busy ? "Saving…" : "Continue"}
              </button>
            </form>

            <div className="lwb-start__fineprint">
              <div className="lwb-start__fineprintRow">
                <span className="lwb-start__lock" aria-hidden="true">
                  🔒
                </span>
                <span>
                  Your email is used to set up your account and track your
                  progress. No spam.
                </span>
              </div>
              <div className="lwb-start__fineprintRow">
                Already have an account? <Link to="/login">Log in</Link>
              </div>
            </div>
          </div>

          {/* Right: benefits */}
          <div className="lwb-start__right">
            <div className="lwb-start__badge">Secure • Private • Simple</div>
            <h2 className="lwb-start__rightTitle">Everything in one safe place</h2>
            <p className="lwb-start__rightSubtitle">
              LastWishBox helps you capture the essentials now, so your family
              has clarity later.
            </p>

            <ul className="lwb-start__list">
              <li>
                <span className="lwb-start__dot" aria-hidden="true" />
                Store key wishes and instructions
              </li>
              <li>
                <span className="lwb-start__dot" aria-hidden="true" />
                Keep important documents organised
              </li>
              <li>
                <span className="lwb-start__dot" aria-hidden="true" />
                Leave scheduled messages for loved ones
              </li>
              <li>
                <span className="lwb-start__dot" aria-hidden="true" />
                Decide who can access what, and when
              </li>
            </ul>

            <div className="lwb-start__miniCard">
              <div className="lwb-start__miniTitle">Takes ~2 minutes</div>
              <div className="lwb-start__miniText">
                Start with your email now — you’ll pick a plan on the next step.
              </div>
            </div>
          </div>
        </div>

        <div className="lwb-start__footer">
          <span>© {new Date().getFullYear()} LastWishBox</span>
        </div>
      </div>
    </div>
  );
}
